import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	redirect,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { CopyIcon, Hexagon } from 'lucide-react'
import { useCopyToClipboard } from 'usehooks-ts'
import { PlotCard } from '#app/components/cards/plot-card'
import { Avatar, AvatarFallback, AvatarImage } from '#app/components/ui/avatar'
import { Button } from '#app/components/ui/button'

import { RESERVED_ROUTES } from '#app/consts'
import { getTreesPlantedByUserId } from '#app/models/donation/get-donation'
import { getPlotsByUserId } from '#app/models/plot/get-plot'
import { getPlotGroupsByUserId } from '#app/models/plot-group/get-plot-group'
import {
	getPublicUserByHandle,
	getUserRank,
} from '#app/models/user/get-user'
import { type FetchedPlot, type FetchedPlotGroup } from '#app/types'
import { authenticator } from '#app/utils/auth.server'
import { shareOrCopy } from '#app/utils/share'

type LoaderData = {
	user: Pick<User, 'id'> | null
	profile: Awaited<ReturnType<typeof getPublicUserByHandle>>
	plots: FetchedPlot[]
	plotGroups: FetchedPlotGroup[]
	treesPlanted: number
	rank: number | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const path = params['*'] as string

	if (RESERVED_ROUTES.includes(path)) return redirect('/')

	const userHandle = path
	const profile = await getPublicUserByHandle(userHandle)

	if (!profile) throw new Response('Profile not found', { status: 404 })

	const plots = await getPlotsByUserId(profile.id)
	const plotGroups = await getPlotGroupsByUserId(profile.id)
	const treesPlanted = await getTreesPlantedByUserId(profile.id)
	const rank = await getUserRank(profile.id)

	const session = await authenticator.isAuthenticated(request)

	if (!session)
		return json<LoaderData>({
			user: null,
			profile,
			plots,
			plotGroups,
			treesPlanted,
			rank,
		})

	const user = { id: session.id }

	return json<LoaderData>({
		user,
		profile,
		plots,
		plotGroups,
		treesPlanted,
		rank,
	})
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
	{ title: `${data?.profile?.handle} | Erda` },
	{
		name: 'description',
		content: `${data?.profile?.handle}'s Places. Your first place is free. Additional paid places help fund tree planting.`,
	},
	{
		name: 'keywords',
		content: 'profile, places, owned',
	},
	{ name: 'og:title', content: `${data?.profile?.handle} | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/profile/${data?.profile?.handle}.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `${data?.profile?.handle}'s Places. Your first place is free. Additional paid places help fund tree planting.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/profile/${data?.profile?.handle}.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `${data?.profile?.handle} | Erda` },
	{
		name: 'twitter:description',
		content: `${data?.profile?.handle}'s Places. Your first place is free. Additional paid places help fund tree planting.`,
	},
]

export default function Profile() {
	// TODO (med) update state with all newly loaded plots, groups etc.

	const loaderData = useLoaderData<LoaderData>()

	const user = loaderData.user
	const profile = loaderData.profile

	const isOwnProfile = user?.id === profile?.id

	const [, copy] = useCopyToClipboard()

	if (!profile) return null

	const plots = loaderData?.plots as Omit<FetchedPlot, 'expiresAt'>[]
	const plotGroups = loaderData?.plotGroups
	const singlePlots = plots.filter(plot => !plot.groupId)

	return (
		<>
			{isOwnProfile && (
				<div className="flex w-full items-center justify-center gap-2">
					<Button data-vaul-no-drag variant="link">
						<Link to="/account">Your account</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link">
						<Link to="/profile/edit">Edit profile</Link>
					</Button>
				</div>
			)}

			<div className="relative flex  w-full items-start justify-between rounded-lg bg-muted p-4">
				<div className="flex items-start gap-3">
					<div className="relative pb-3">
						<Avatar className="h-16 w-16 rounded">
							<AvatarImage
								src={profile.imageUrl ?? ''}
								className="flex h-full w-full items-center justify-center rounded-full bg-background object-cover"
							/>
							<AvatarFallback className="text-2xl uppercase">
								{profile.handle.slice(0, 1)}
							</AvatarFallback>
						</Avatar>
						{loaderData.treesPlanted > 0 && (
							<Link
								to="/tree-planting"
								className="mt-2 block text-xs text-muted-foreground"
							>
								{loaderData.treesPlanted} trees funded
							</Link>
						)}
					</div>

					<div
						className="flex flex-col gap-2"
						style={{
							wordBreak: 'break-word',
						}}
					>
						<div className="flex w-full flex-col items-start gap-1">
							<h1 className="flex items-center gap-2 text-xl">
								<span>{profile.handle}</span>
								{loaderData.rank && (
									<Link
										to="/leaderboard"
										className="flex items-center gap-0.5 rounded-full border border-border bg-background px-2 py-0.5 text-xs font-bold text-muted-foreground"
									>
										<span>Rank #{loaderData.rank}</span>
									</Link>
								)}
							</h1>
							<Button
								data-vaul-no-drag
								variant="link"
								size="sm"
								className="group -mt-1 gap-1 text-sm text-muted-foreground"
								onClick={e => {
									e.stopPropagation()
									e.preventDefault()
									void shareOrCopy(
										{
											title: `${profile.handle} on Erda`,
											text: `Check out @${profile.handle}'s profile and places on Erda!`,
											url: `https://erda.app/${profile.handle}`,
										},
										copy,
									)
								}}
							>
								erda.app/{profile.handle}
								<CopyIcon className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100" />
							</Button>
						</div>
						{profile.website && (
							<a
								className="text-sm underline"
								target="_blank"
								rel="noreferrer"
								href={profile.website}
							>
								{profile.website}
							</a>
						)}
						{profile.bio && (
							<p
								className="text-sm"
								style={{
									wordBreak: 'break-word',
								}}
							>
								{profile.bio}
							</p>
						)}
					</div>
				</div>
			</div>

			<div className="flex w-full flex-col gap-2">
				{plotGroups.map(group => (
					<PlotCard
						key={group.id}
						type="group"
						id={group.id}
						handle={group.handle}
					/>
				))}

				{singlePlots.map(({ plotId, handle, subscriptionId, ...rest }) => (
					<PlotCard
						key={plotId}
						type="plot"
						id={plotId}
						handle={handle}
						note={rest.note}
					/>
				))}

				{plotGroups.length === 0 && singlePlots.length === 0 && (
					<div className="my-2 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-8 text-center">
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-50 text-purple-600 dark:bg-purple-950/50 dark:text-purple-400">
							<Hexagon className="h-6 w-6 fill-purple-200/50 stroke-[1.5]" />
						</div>
						<div className="flex flex-col gap-1">
							<h3 className="text-sm font-bold text-foreground">
								No places yet
							</h3>
							<p className="max-w-xs text-xs text-muted-foreground">
								{isOwnProfile
									? 'Claim your first place for free and share why it matters to you.'
									: `@${profile.handle} hasn’t claimed any places yet.`}
							</p>
						</div>
						{isOwnProfile && (
							<Button size="sm" asChild>
								<Link to="/">Explore the Map</Link>
							</Button>
						)}
					</div>
				)}
			</div>
		</>
	)
}
